import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const t = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export async function downloadBlob(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
    return true;
  } catch { toast.error("Could not generate the report"); return false; }
}

export function DailyReportButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    const d = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    if (await downloadBlob("/admin-reports/daily-site/pdf", `daily_site_summary_${d}.pdf`)) toast.success("Daily report downloaded");
    setBusy(false);
  };
  return (
    <Button className="gap-1.5" onClick={run} disabled={busy} data-testid="daily-report-btn">
      {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.FileBarChart className="h-4 w-4" />}Daily Report
    </Button>
  );
}

function Row({ g, right }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-0 text-sm" data-testid={`overnight-row-${g.id}`}>
      <div className="min-w-0">
        <div className="font-medium truncate">{g.person_name}{g.is_test && <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-800 text-[10px]">TEST</Badge>}</div>
        <div className="text-xs text-muted-foreground">{g.pass_number} · Entry {t(g.entry_at)}</div>
      </div>
      <div className="text-xs text-right shrink-0">{right}</div>
    </div>
  );
}

export function OvernightReportCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin-reports/overnight").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const pdf = () => downloadBlob("/admin-reports/overnight/pdf", `overnight_auto_exit_${d.date}.pdf`);
  return (
    <Card className="p-5" data-testid="overnight-report-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-700"><Icons.MoonStar className="h-4 w-4" /></div>
          <div><div className="font-display font-semibold">Overnight Auto-Exit Report</div><div className="text-xs text-muted-foreground">{new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · Admin only</div></div>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={pdf} data-testid="overnight-pdf-btn"><Icons.FileDown className="h-4 w-4" />PDF</Button>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Auto-exited overnight <b className="text-foreground" data-testid="overnight-auto-count">{d.auto_exited.length}</b></div>
          {d.auto_exited.length ? d.auto_exited.map((g) => <Row key={g.id} g={g} right={<span className="text-indigo-700">Auto-exit {t(g.exit_at)}</span>} />)
            : <p className="text-sm text-muted-foreground py-2" data-testid="overnight-auto-empty">No entries were auto-exited last night.</p>}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Vehicles needing admin exit <b className="text-foreground" data-testid="overnight-vehicle-count">{d.vehicles_pending.length}</b></div>
          {d.vehicles_pending.length ? d.vehicles_pending.map((g) => <Row key={g.id} g={g} right={<span className="text-red-700 font-medium">{g.vehicle_number}</span>} />)
            : <p className="text-sm text-muted-foreground py-2" data-testid="overnight-vehicle-empty">No vehicles are waiting for a manual exit.</p>}
        </div>
      </div>
    </Card>
  );
}
